import React, { useCallback, useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Select } from "antd";
import { Icon } from "components/hds";
import { Spinner } from "components/ui";

import { useTranslation } from "react-i18next";
import useProjects from "contexts/Projects/useProjects";
import { PROJECTS, SCENES } from "common/routes.constants";
import MenuItem from "components/MenuItem/MenuItem";
import MenuItemStyles from "components/MenuItem/MenuItem.styles";
import {
	StyledSwitcherContainer,
	StyledSwitcherNoMatch,
} from "common/styles/Switcher.styles";
import useScenes from "contexts/Scenes/useScenes";
import useConfirmLeaveScene from "hooks/useConfirmLeaveScene";
import useSwitcherSearch, { isUnfilteredQuery } from "hooks/useSwitcherSearch";
import { SCENE_SEARCH_FIELDS } from "contexts/Scenes/searchFields";
import { Scene } from "contexts/Scenes/types";

const { Option } = Select;

const SEARCH_FIELDS_QUERY = SCENE_SEARCH_FIELDS.map(
	(field) => `search[fields]=${field}`,
).join("&");

const SceneSwitcher = () => {
	const { search } = useLocation();
	const history = useHistory();
	const { current: currentProject, loading: loadingProjects } = useProjects();
	const {
		scenes,
		current,
		loading,
		loadMore,
		query,
		pagedResponse,
	} = useScenes();
	const { t } = useTranslation();
	const confirmLeaveScene = useConfirmLeaveScene();

	const [switcherState, setSwitcherState] = useState("");
	const selectRef = useRef<any>(null);

	// Typing filters the loaded scenes at once and asks the server for the
	// rest of the project's scenes (see useSwitcherSearch).
	const buildSearchUrl = useCallback(
		(text: string) =>
			currentProject
				? `/projects/${currentProject._id}/scenes?limit=100&page=1&sort=meta.name&search[text]=${encodeURIComponent(text)}&${SEARCH_FIELDS_QUERY}`
				: null,
		[currentProject],
	);
	const sceneSearch = useSwitcherSearch<Scene>({
		buildUrl: buildSearchUrl,
		getLabel: (scene) => scene.meta.name || "",
		// The same fields as the scenes gallery, so both find the same scenes.
		searchFields: SCENE_SEARCH_FIELDS,
		getKey: (scene) => scene.shortid,
		loadedItems: scenes ?? [],
		loadedComplete:
			Boolean(pagedResponse) &&
			!pagedResponse?.hasNextPage &&
			isUnfilteredQuery(query),
	});
	const serverSearch = sceneSearch.isSearching;

	useEffect(() => {
		setSwitcherState(current?.shortid || "");
		
		// Blur the select after scene change to prevent it from capturing keyboard input
		if (selectRef.current) {
			setTimeout(() => {
				selectRef.current?.blur?.();
			}, 100);
		}
	}, [current]);

	if (!currentProject || loadingProjects) {
		return <span>{/* Do not delete this span */}</span>;
	}

	return (
		<StyledSwitcherContainer className="sceneSwitcher">
			<MenuItemStyles />
			<span className="label">
				{String(t("app.SceneSwitcher.label.switcher"))}
			</span>
			<Select
				ref={selectRef}
				value={switcherState}
				/* No inline width: it would beat the wider one in
				   Switcher.styles.tsx, which is what carries the chip's hit area
				   under the chevron. */

				/* Switching scenes discards whatever the open one has not saved,
				   so the same confirmation as the header logo guards it. On
				   cancel the Select snaps back on its own: `value` follows
				   switcherState, which only moves when `current` does. */
				onChange={(shortid: string) => {
					confirmLeaveScene(() =>
						history.push({
							pathname: `/${PROJECTS}/${currentProject?.shortid}/${SCENES}/${shortid}`,
							search,
						}),
					);
				}}
				bordered={false}
				popupClassName="header-select-dropdown"
				showSearch
				searchValue={sceneSearch.searchText}
				onSearch={sceneSearch.setSearchText}
				filterOption={false}
				onOpenChange={(open) => {
					if (!open) {
						sceneSearch.reset();
						// The search input is a real text field now. Closing the
						// list without picking a scene must not leave it holding
						// the focus, or the editor's walk keys (WASD) would type
						// into it instead.
						selectRef.current?.blur?.();
					}
				}}
				loading={sceneSearch.loading}
				notFoundContent={
					sceneSearch.loading ? (
						<Spinner maxSize="25px" />
					) : (
						<StyledSwitcherNoMatch>
							{String(t("app.SceneSwitcher.label.noMatch"))}
						</StyledSwitcherNoMatch>
					)
				}
				/* The list is where names must not truncate, so it sizes to its
				   content. rc-select keeps the trigger width as a minimum; the cap
				   is in MenuItem.styles.tsx. */
				popupMatchSelectWidth={false}
				suffixIcon={<Icon name="ArrowDownFilledIcon" />}
				className="header-select"
				virtual
				onPopupScroll={(
					event: React.UIEvent<HTMLDivElement, UIEvent>,
				) => {
					const {
						scrollTop,
						offsetHeight,
						scrollHeight,
					} = event.target as HTMLElement;
					const shouldLoadMore =
						scrollTop + offsetHeight === scrollHeight;

					if (shouldLoadMore && !loading && loadMore && !serverSearch) {
						loadMore();
					}
				}}
			>
				{serverSearch &&
					(sceneSearch.results ?? []).map((scene) => (
						<Option key={scene.shortid} value={scene.shortid}>
							<MenuItem title={scene.meta.name || ''} />
						</Option>
					))}
				{!serverSearch && loading && (
					<Option key="loading1" value="">
						<MenuItem title="Loading scenes" />
					</Option>
				)}
				{!serverSearch && (
					<Option value="">
						<MenuItem title={String(t("app.SceneSwitcher.label.allScenes"))} />
					</Option>
				)}
				{!serverSearch &&
					scenes?.map((scene) => (
						<Option key={scene.shortid} value={scene.shortid}>
							<MenuItem title={scene.meta.name || ''} />
						</Option>
					))}
				{!serverSearch && loading && (
					<Option key="loading2" value="">
						<MenuItem title="Loading scenes" />
					</Option>
				)}
			</Select>
		</StyledSwitcherContainer>
	);
};

export default SceneSwitcher;
